import { useCallback } from "react";
import { PageBody } from "../PageBody";
import styled from "styled-components";
import {
  DataListBody,
  DataListBodyItem,
  DataListHeader,
  DataListHeaderItem,
  DataListRow,
  DataTable,
  SwitchHeaderOrder,
} from "../../atoms/Table";
import { Typography } from "../../atoms/Typography";
import { GQLQueryOrderInput } from "@rx-cs-system/schema";
import { LoadMoreButton } from "../../atoms/LoadMoreButton";
import { SearchInput } from "../../atoms/SearchInput";

type ColumnName =
  | "id"
  | "nickname"
  | "appName"
  | "prescriptionCode"
  | "prescribedAt";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 40px;
  height: 100%;
`;

const NameWrapper = styled.div`
  max-width: 100%;
`;

const AppWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 204px;
  img {
    flex-shrink: 0;
    margin-right: 8px;
  }
`;

type PatientHaudy = {
  id: string;
  nickname: string;
  appName: string;
  appImage: string;
  prescriptionCode?: string;
  prescribedAt?: string;
};

export type PatientHaudyPageProps = {
  patients: PatientHaudy[];
  searchValue: string;
  order?: GQLQueryOrderInput;
  onSearchClick: (keyword: string) => void;
  onResetClick: () => void;
  onSortClick: (input: GQLQueryOrderInput) => void;
  onRowClick: (id: string) => void;
  onLoadMoreClick: (skip: number) => void;
};

export function PatientHaudyPage({
  patients,
  searchValue,
  order,
  onSearchClick,
  onResetClick,
  onSortClick,
  onRowClick,
  onLoadMoreClick,
}: PatientHaudyPageProps) {
  const sortClick = useCallback(
    (value: ColumnName) => {
      onSortClick(SwitchHeaderOrder(value, order));
    },
    [onSortClick, order],
  );

  const header = (
    <div>
      <DataListHeaderItem
        onSort={() => sortClick("nickname")}
        order={order?.field === "nickname" ? order.value : undefined}
      >
        <Typography size={1} bold>
          ニックネーム
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem>
        <Typography size={1} bold>
          アプリ名
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("prescriptionCode")}
        order={order?.field === "prescriptionCode" ? order.value : undefined}
      >
        <Typography size={1} bold>
          処方コード
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("prescribedAt")}
        order={order?.field === "prescribedAt" ? order.value : undefined}
      >
        <Typography size={1} bold>
          処方日
        </Typography>
      </DataListHeaderItem>
    </div>
  );

  const searchInput = (
    <SearchInput
      value={searchValue}
      placeholder={"キーワードを入力"}
      onSearch={onSearchClick}
      onReset={onResetClick}
    />
  );

  return (
    <PageBody title="処方一覧" searchInput={searchInput}>
      <Container>
        <DataTable>
          <DataListHeader row={header} flexRatio={[3, 3, 2, 1]} />
          <DataListBody
            rows={patients.map((patient: PatientHaudy) => (
              <DataListRow
                flexRatio={[3, 3, 2, 1]}
                key={patient.id}
                onRowClick={() => onRowClick(patient.id)}
              >
                <DataListBodyItem>
                  <NameWrapper>
                    <Typography size={2} withEllipsis={true}>
                      {patient.nickname}
                    </Typography>
                  </NameWrapper>
                </DataListBodyItem>
                <DataListBodyItem>
                  <AppWrapper>
                    <img
                      src={patient.appImage}
                      width="36"
                      height="36"
                      alt={"app_logo"}
                    />
                    <Typography size={2} withEllipsis={true}>
                      {patient.appName}
                    </Typography>
                  </AppWrapper>
                </DataListBodyItem>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {patient.prescriptionCode}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {patient.prescribedAt}
                  </Typography>
                </DataListBodyItem>
              </DataListRow>
            ))}
          />
        </DataTable>
        <LoadMoreButton onClick={() => onLoadMoreClick(patients.length)} />
      </Container>
    </PageBody>
  );
}
